import { type RecommendationsTab_userProfile$key } from '#app/__generated__/relay/RecommendationsTab_userProfile.graphql.js';
import { type useUserInterfaceFlags_fragment$key } from '#app/__generated__/relay/useUserInterfaceFlags_fragment.graphql.js';
import { RoutingTabs } from '#app/components/RoutingTabs/RoutingTabs.js';
import { RecommendationsTabTooltipWrapper } from '#app/features/profile/components/ProfileRecommendations/RecommendationsTabTooltipWrapper.js';
import { resolveRecommendationsVisibility } from '#app/features/profile/components/ProfileRecommendations/utils.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useRouter } from '#app/hooks/useRouter.js';
import { useUserInterfaceFlags } from '#app/hooks/useUserInterfaceFlags.js';
import { IconContainer } from '../ProfileLayout.styles.js';
import { EyeOffIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { useRef } from 'react';

type RecommendationsTabProps = {
  readonly accountRef?: null | useUserInterfaceFlags_fragment$key;
  readonly userProfileRef: RecommendationsTab_userProfile$key;
};

export const RecommendationsTab = ({
  accountRef,
  userProfileRef,
}: RecommendationsTabProps) => {
  const userProfile = useFragment<RecommendationsTab_userProfile$key>(
    graphql`
      fragment RecommendationsTab_userProfile on UserProfile {
        analyticsId
        visitorCanEdit
        displayUsername
        receivedTestimonials {
          edges {
            node {
              recommend
            }
          }
        }
        recommendationRequests {
          count
        }
      }
    `,
    userProfileRef,
  );

  const {
    analyticsId,
    displayUsername,
    receivedTestimonials,
    recommendationRequests,
    visitorCanEdit,
  } = userProfile;
  const { isActive } = useRouter();
  const { track } = useAnalytics();
  const flags = useUserInterfaceFlags({ accountRef });

  const positiveRecommendationsCount =
    receivedTestimonials?.edges?.filter?.(
      ({ node: { recommend } }) => recommend,
    )?.length ?? 0;

  const totalPositiveRecommendationsCount =
    positiveRecommendationsCount + recommendationRequests.count;

  const { isRecommendationsTabOnlyVisibleByOwner } =
    resolveRecommendationsVisibility({
      hasPositiveRecommendations: totalPositiveRecommendationsCount > 0,
      visitorCanEdit,
    });

  const recommendationsTabRef = useRef<HTMLLIElement>(null);

  return (
    <RoutingTabs.Tab
      match={() =>
        isActive(
          getRoute('/@username/recommendations', {
            args: {
              username: displayUsername,
            },
          }),
          false,
        )
      }
      onClick={() => {
        if (userProfile) {
          track('tab', {
            action: 'tapped',
            is_owner: visitorCanEdit,
            owner_id: analyticsId,
            tab_name: 'profile_recommendations',
          });
        }
      }}
      ref={recommendationsTabRef}
      to={getRoute('/@username/recommendations', {
        args: {
          username: displayUsername,
        },
      })}
    >
      <RecommendationsTabTooltipWrapper
        flags={flags}
        isRecommendationsTabOnlyVisibleByOwner={
          isRecommendationsTabOnlyVisibleByOwner
        }
        positiveRecommendationsOnCurrentProfileCount={
          totalPositiveRecommendationsCount
        }
        recommendationsTabRef={recommendationsTabRef}
        visitorCanEdit={visitorCanEdit}
      >
        <IconContainer>
          {isRecommendationsTabOnlyVisibleByOwner && <EyeOffIcon />}
          Recommendations
        </IconContainer>
      </RecommendationsTabTooltipWrapper>
    </RoutingTabs.Tab>
  );
};
